import React, { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';

// ============ TYPES ============

interface SignatureMoveDefinition {
  id: string;
  baseName: string;
  powerCost: number;
  color: string;
}

interface PlayerSignatureMove {
  definitionId: string;
  customAlias: string;
  isEquipped: boolean;
  definition?: SignatureMoveDefinition;
}

interface SignaturePowerBarProps {
  socket: Socket | null;
}

// ============ STYLES ============

const injectStyles = (): void => {
  const styleId = 'signature-power-bar-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .signature-power-bar {
      position: fixed;
      bottom: 120px;
      right: 20px;
      font-family: 'Sometype Mono', monospace;
      z-index: 100;
    }
    
    .signature-power-bar-container {
      background: rgba(10, 10, 26, 0.9);
      border: 1px solid #2a2a4e;
      padding: 8px 12px;
      min-width: 160px;
    }
    
    .signature-power-bar-label {
      font-size: 9px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
      display: block;
    }
    
    .signature-power-bar-display {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      font-size: 11px;
    }
    
    .signature-power-bar-percent {
      font-weight: bold;
      color: #4fc3f7;
    }
    
    .signature-power-bar-percent.critical {
      color: #ff6b6b;
    }
    
    .signature-power-bar-percent.warning {
      color: #ffa500;
    }
    
    .signature-power-bar-percent.optimal {
      color: #4ade80;
    }
    
    .signature-power-bar-regen {
      font-size: 8px;
      color: #888;
    }
    
    .signature-power-bar-fill-container {
      width: 100%;
      height: 12px;
      background: #1a1a2e;
      border: 1px solid #2a2a4e;
      position: relative;
      overflow: hidden;
    }
    
    .signature-power-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #4fc3f7, #29b6f6);
      transition: width 0.1s linear;
      width: 100%;
      position: relative;
    }
    
    .signature-power-bar-fill.critical {
      background: linear-gradient(90deg, #ff6b6b, #ff4444);
    }
    
    .signature-power-bar-fill.warning {
      background: linear-gradient(90deg, #ffa500, #ff8c00);
    }
    
    .signature-power-bar-fill.optimal {
      background: linear-gradient(90deg, #4ade80, #22c55e);
    }
    
    .signature-power-bar-fill::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, 
        transparent 0%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 100%
      );
      animation: shimmer 2s infinite;
    }
    
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    
    .signature-power-bar-equipped {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #2a2a4e;
      font-size: 9px;
      color: #888;
    }
    
    .signature-power-bar-move-name {
      font-weight: bold;
      margin-bottom: 3px;
      word-break: break-word;
    }
    
    .signature-power-bar-move-cost {
      font-size: 8px;
      color: #4fc3f7;
      margin-top: 2px;
    }
    
    .signature-power-bar-insufficient {
      font-size: 8px;
      color: #ff6b6b;
      margin-top: 3px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .signature-power-bar-insufficient::before {
      content: '⚠';
      font-size: 9px;
    }
    
    .signature-power-bar-regenerating {
      font-size: 8px;
      color: #4ade80;
      margin-top: 3px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .signature-power-bar-regenerating::before {
      content: '⚡';
      font-size: 9px;
      animation: pulse 1s infinite;
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `;
  document.head.appendChild(style);
};

// ============ COMPONENT ============

export const SignaturePowerBar: React.FC<SignaturePowerBarProps> = ({ socket }) => {
  const [power, setPower] = useState<number>(100);
  const [maxPower, setMaxPower] = useState<number>(100);
  const [equippedMove, setEquippedMove] = useState<PlayerSignatureMove | null>(null);
  const [insufficientPower, setInsufficientPower] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  const regenRate = '1.0%/s'; // Power regeneration rate display

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Fetch signature moves on mount
  useEffect(() => {
    if (socket) {
      socket.emit('wildcard:list');
    }
  }, [socket]);

  // Listen for signature events
  useEffect(() => {
    if (!socket) return;

    const handleList = (data: {
      moves: PlayerSignatureMove[];
      equipped: string | null;
      power: number;
      maxPower: number;
    }): void => {
      setPower(data.power || 100);
      setMaxPower(data.maxPower || 100);
      
      const equipped = data.moves.find(m => m.isEquipped);
      setEquippedMove(equipped || null);
      setInsufficientPower(false);
    };

    const handleActivated = (data: {
      powerRemaining: number;
      maxPower: number;
    }): void => {
      setPower(data.powerRemaining || 0);
      setMaxPower(data.maxPower || 100);
      setInsufficientPower(false);
    };

    const handleError = (data: {
      powerInsufficient?: boolean;
      currentPower?: number;
      requiredPower?: number;
      message?: string;
    }): void => {
      if (data.powerInsufficient) {
        setInsufficientPower(true);
        if (data.currentPower !== undefined) {
          setPower(data.currentPower);
        }
      }
    };

    const handleEquipped = (): void => {
      socket.emit('wildcard:list');
    };

    socket.on('wildcard:list', handleList);
    socket.on('wildcard:activated', handleActivated);
    socket.on('wildcard:error', handleError);
    socket.on('wildcard:equipped', handleEquipped);

    return () => {
      socket.off('wildcard:list', handleList);
      socket.off('wildcard:activated', handleActivated);
      socket.off('wildcard:error', handleError);
      socket.off('wildcard:equipped', handleEquipped);
    };
  }, [socket]);

  // Simulate power regeneration display
  useEffect(() => {
    if (power < maxPower) {
      setIsRegenerating(true);
      const interval = setInterval(() => {
        setPower(prev => {
          const nextPower = Math.min(prev + 1.0 / 10, maxPower); // 1% per second = 0.1% per 100ms
          if (nextPower >= maxPower) {
            setIsRegenerating(false);
          }
          return nextPower;
        });
      }, 100);

      return () => clearInterval(interval);
    } else {
      setIsRegenerating(false);
    }
   }, [maxPower, power]);

  // Determine power level for color coding
  const getStatusClass = (): string => {
    const percent = (power / maxPower) * 100;
    if (percent < 25) return 'critical';
    if (percent < 50) return 'warning';
    return 'optimal';
  };

  // Determine if equipped move can be activated
  const canActivate = equippedMove?.definition ? power >= equippedMove.definition.powerCost : false;

  return (
    <div className="signature-power-bar">
      <div className="signature-power-bar-container">
        <span className="signature-power-bar-label">Signature Power</span>

        <div className="signature-power-bar-display">
          <span className={`signature-power-bar-percent ${getStatusClass()}`}>
            {Math.floor(power)}%
          </span>
          <span className="signature-power-bar-regen">{regenRate}</span>
        </div>

        <div className="signature-power-bar-fill-container">
          <div
            className={`signature-power-bar-fill ${getStatusClass()}`}
            style={{ width: `${(power / maxPower) * 100}%` }}
          />
        </div>

        {equippedMove && equippedMove.definition && (
          <div className="signature-power-bar-equipped">
            <div className="signature-power-bar-move-name" style={{ color: equippedMove.definition.color }}>
              {equippedMove.customAlias}
            </div>
            <div className="signature-power-bar-move-cost">
              Cost: {equippedMove.definition.powerCost}%
            </div>

            {insufficientPower && !canActivate && (
              <div className="signature-power-bar-insufficient">
                Need {equippedMove.definition.powerCost}%
              </div>
            )}

            {isRegenerating && (
              <div className="signature-power-bar-regenerating">
                Regenerating
              </div>
            )}

            {canActivate && !insufficientPower && (
              <div style={{ fontSize: '8px', color: '#4ade80', marginTop: '3px' }}>
                ✓ Ready to activate
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignaturePowerBar;
